/**
 * TimelineItemFilmStripV2
 *
 * 기존 TimelineItemFilmStrip의 성능 개선 버전
 *
 * 개선 사항:
 * - 에셋 단위로 프레임을 한 번만 추출 (중복 추출 방지)
 * - AssetFramesContext에서 미리 추출된 프레임 사용
 * - 프레임 추출 중일 때 shimmer 효과 표시
 * - 비디오 아이템의 videoStartFromInSeconds 기준으로 프레임 표시
 *
 * 사용 방법:
 * - 기존 TimelineItemFilmStrip 대신 사용
 * - AssetFramesProvider가 상위에 있어야 함
 */

import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { VideoItem } from '../../items/video/video-item-type';
import { useAssetFromItem, useTimelineContext } from '../../utils/use-context';
import { useTimelineSize } from '../utils/use-timeline-size';
import { getVisibleFrames } from '../../utils/get-visible-frames';
import { useAssetFrames } from '../../contexts/asset-frames-context';
import {
  ExtractedFrame,
  findFramesInRange,
  FRAME_THUMBNAIL_HEIGHT,
} from '../../caching/asset-frame-extractor';

// 디스플레이 간 이동 시 버그를 방지하기 위해 component 외부에 이 값을 유지합니다.
const DEVICE_PIXEL_RATIO =
  typeof window !== 'undefined' ? window.devicePixelRatio : 1;

// video item이 있고 audio가 없는 경우 더 높을 수 있으며, 그럴 때 item 높이를 채웁니다.
export const FILMSTRIP_HEIGHT_IF_THERE_IS_AUDIO_V2 = FRAME_THUMBNAIL_HEIGHT;

interface TimelineItemFilmStripV2Props {
  readonly item: VideoItem;
  readonly startFrom: number;
  readonly durationInFrames: number;
  readonly fps: number;
  readonly roundedDifference: number;
  readonly height: number;
  readonly playbackRate: number;
}

/**
 * 프레임을 Canvas에 그리기
 */
function drawFramesToCanvas(
  canvas: HTMLCanvasElement,
  frames: ExtractedFrame[],
  startTimeInSeconds: number,
  endTimeInSeconds: number,
  pxPerSecond: number,
  height: number,
  aspectRatio: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // 캔버스 초기화
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // 프레임이 없으면 리턴
  if (frames.length === 0) return;

  const widthOfOneFrame = Math.round(height * aspectRatio);
  const durationInSeconds = endTimeInSeconds - startTimeInSeconds;

  // 필요한 프레임 수 계산
  const totalWidth = durationInSeconds * pxPerSecond;
  const framesNeeded = Math.ceil(totalWidth / widthOfOneFrame) + 1;

  // 해당 범위의 프레임들 찾기
  const rangeFrames = findFramesInRange(
    frames,
    startTimeInSeconds,
    endTimeInSeconds,
  );

  if (rangeFrames.length === 0) return;

  // 각 슬롯에 프레임 그리기
  for (let i = 0; i < framesNeeded; i++) {
    const slotTimeInSeconds =
      startTimeInSeconds + (i * widthOfOneFrame) / pxPerSecond;

    // 가장 가까운 프레임 찾기
    let closestFrame: ExtractedFrame | null = null;
    let minDistance = Infinity;

    for (const frame of rangeFrames) {
      const distance = Math.abs(frame.timestampInSeconds - slotTimeInSeconds);
      if (distance < minDistance) {
        minDistance = distance;
        closestFrame = frame;
      }
    }

    if (!closestFrame) continue;

    // 프레임 그리기
    const x = i * widthOfOneFrame;
    try {
      ctx.drawImage(closestFrame.imageBitmap, x, 0, widthOfOneFrame, height);
    } catch (e) {
      // ImageBitmap이 닫혔을 수 있음
      console.warn('Failed to draw frame:', e);
    }
  }
}

/**
 * Shimmer 효과 컴포넌트
 */
function ShimmerOverlay({ height }: { height: number }) {
  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{ height }}
    >
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/10 to-transparent"
        style={{
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
        }}
      />
      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Inner FilmStrip 컴포넌트 (실제 렌더링)
 */
const InnerTimelineItemFilmStripV2: React.FC<{
  readonly assetId: string;
  readonly aspectRatio: number;
  readonly pxPerSecond: number;
  readonly fromSeconds: number;
  readonly toSeconds: number;
  readonly roundedDifference: number;
  readonly height: number;
}> = ({
  assetId,
  aspectRatio,
  pxPerSecond,
  fromSeconds,
  toSeconds,
  roundedDifference,
  height,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Context에서 프레임 데이터 가져오기
  const { frames, isExtracting, isDone } = useAssetFrames(assetId);

  // 실제 추출된 프레임의 aspect ratio 사용 (에셋 메타데이터와 다를 수 있음)
  // 예: 16:9 인코딩 + 회전 메타데이터인 경우 asset은 9:16이지만 실제 프레임은 16:9
  const effectiveAspectRatio = useMemo(() => {
    const firstFrame = frames[0];
    if (firstFrame) {
      return firstFrame.imageBitmap.width / firstFrame.imageBitmap.height;
    }
    return aspectRatio;
  }, [frames, aspectRatio]);

  // 캔버스 크기 계산
  const widthOfOneFrame = Math.round(height * effectiveAspectRatio);
  const durationInSeconds = toSeconds - fromSeconds;
  const framesNeeded =
    Math.ceil((durationInSeconds * pxPerSecond) / widthOfOneFrame) + 1;
  const visualizationWidth = widthOfOneFrame * framesNeeded;

  // 필름스트립은 아이템 내부 왼쪽 끝에서 시작
  // roundedDifference는 아이템 위치 반올림 보정용
  const xOfFirstSlot = -roundedDifference;

  // 캔버스에 프레임 그리기
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 캔버스가 없으면 생성
    if (!canvasRef.current) {
      const canvas = document.createElement('canvas');
      canvas.style.width = `${Math.ceil(visualizationWidth)}px`;
      canvas.style.height = `${height}px`;
      canvas.width = Math.ceil(visualizationWidth * DEVICE_PIXEL_RATIO);
      canvas.height = Math.ceil(height * DEVICE_PIXEL_RATIO);
      container.appendChild(canvas);
      canvasRef.current = canvas;

      // 고해상도 디스플레이를 위한 스케일링
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(DEVICE_PIXEL_RATIO, DEVICE_PIXEL_RATIO);
      }
    } else {
      // 캔버스 크기 업데이트
      const canvas = canvasRef.current;
      canvas.style.width = `${Math.ceil(visualizationWidth)}px`;
      canvas.style.height = `${height}px`;
      canvas.width = Math.ceil(visualizationWidth * DEVICE_PIXEL_RATIO);
      canvas.height = Math.ceil(height * DEVICE_PIXEL_RATIO);

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(DEVICE_PIXEL_RATIO, DEVICE_PIXEL_RATIO);
      }
    }

    // 프레임 그리기
    if (frames.length > 0) {
      drawFramesToCanvas(
        canvasRef.current,
        frames,
        fromSeconds,
        toSeconds,
        pxPerSecond,
        height,
        effectiveAspectRatio,
      );
    }

    return () => {
      // cleanup: 캔버스는 유지 (재사용)
    };
  }, [
    frames,
    fromSeconds,
    toSeconds,
    pxPerSecond,
    height,
    effectiveAspectRatio,
    visualizationWidth,
  ]);

  // 컴포넌트 언마운트 시 캔버스 제거
  useLayoutEffect(() => {
    return () => {
      if (canvasRef.current && containerRef.current) {
        containerRef.current.removeChild(canvasRef.current);
        canvasRef.current = null;
      }
    };
  }, []);

  const containerStyle = useMemo<React.CSSProperties>(
    () => ({
      height,
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      userSelect: 'none',
      pointerEvents: 'none',
      marginLeft: xOfFirstSlot,
    }),
    [height, xOfFirstSlot],
  );

  return (
    <div
      ref={containerRef}
      style={containerStyle}
    >
      {/* 프레임 추출 중일 때 shimmer 효과 */}
      {isExtracting && !isDone && <ShimmerOverlay height={height} />}
    </div>
  );
};

/**
 * TimelineItemFilmStripV2 메인 컴포넌트
 */
export const TimelineItemFilmStripV2: React.FC<
  TimelineItemFilmStripV2Props
> = ({
  item,
  startFrom,
  durationInFrames,
  fps,
  roundedDifference,
  height,
  playbackRate,
}) => {
  const { timelineWidth } = useTimelineSize();
  const { durationInFrames: totalDurationInFrames } = useTimelineContext();
  const asset = useAssetFromItem(item);

  const visibleFrames = getVisibleFrames({
    fps,
    totalDurationInFrames,
  });

  if (timelineWidth === null) {
    throw new Error('Timeline width is null');
  }

  if (asset.type !== 'video') {
    throw new Error('Asset is not a video');
  }

  // 비디오 아이템의 원본 시작/종료 시간 (초)
  const fromSeconds = startFrom / fps;
  const toSeconds = (startFrom + durationInFrames * playbackRate) / fps;

  // 타임라인에서의 픽셀당 초
  const pxPerSecond = timelineWidth / (visibleFrames / fps) / playbackRate;

  return (
    <InnerTimelineItemFilmStripV2
      assetId={asset.id}
      aspectRatio={asset.width / asset.height}
      pxPerSecond={pxPerSecond}
      fromSeconds={fromSeconds}
      toSeconds={toSeconds}
      roundedDifference={roundedDifference}
      height={height}
    />
  );
};

export default TimelineItemFilmStripV2;
